/**
 * A pending plan proposal (spec 2026-10-21-11 §8): who made it, each layer's
 * discipline and registration residual, the floor's placement, the bundle with
 * its broken rooms in red and its issues by kind. It reuses the digitization
 * preview's pattern: Accept, Keep only the valid rooms, Discard, and Replace
 * when the bundle supersedes a live one.
 */
import { FLOORPLAN_ISSUE_CODES } from './engine/validate'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { $api } from '../../api'
import { useFormatters } from '../../lib/format'
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { PlanPreviewThumbnail } from './PlanPreviewThumbnail'
import { apiErrorMessage } from './useDigitizePlan'
import type { PlanProposal } from './usePlanProposals'

interface Props {
    org: string
    /** The pending proposals, oldest first: the dialog shows the first. */
    proposals: PlanProposal[]
    open: boolean
    onClose: () => void
    /** After a decision: refetch the list (and, after an accept, the scene). */
    onDecided: (accepted: boolean) => void | Promise<void>
    /** The name of an existing layer, by representation uid. */
    layerLabelOf: (repUid: string) => string | undefined
}

interface StoredLayer {
    representation_uid?: string
    document_uid?: string
    page?: number
    title?: string
    discipline?: string
}

interface StoredBundle {
    spaces?: { id: string; name?: string; boundary?: { x: number; y: number }[] }[]
    walls?: unknown[]
    openingEquipments?: unknown[]
    equipments?: unknown[]
}

const ACCEPT_PATH = '/api/v1/organizations/{orgCode}/plan-proposals/{proposalUid}/accept' as const
const DISCARD_PATH = '/api/v1/organizations/{orgCode}/plan-proposals/{proposalUid}/discard' as const

export function PlanProposalDialog({ org, proposals, open, onClose, onDecided, layerLabelOf }: Props) {
    const { t } = useTranslation('assets')
    const { formatDate } = useFormatters()
    const proposal = proposals[0]
    const accept = $api.useMutation('post', ACCEPT_PATH)
    const discard = $api.useMutation('post', DISCARD_PATH)
    const busy = accept.isPending || discard.isPending
    // The last refusal, and which proposal it was about.
    const [refusal, setRefusal] = useState<{ uid: string; code: string; message: string } | null>(null)
    const current = refusal && proposal && refusal.uid === proposal.uid ? refusal : null

    const value = (proposal?.value ?? {}) as {
        calibration?: { mm_per_unit?: number }
        layers?: StoredLayer[]
        vector?: { value?: StoredBundle }
    }
    const validation = proposal?.validation
    const bundle = value.vector?.value
    const spaces = useMemo(() => bundle?.spaces ?? [], [bundle])
    const broken = useMemo(() => new Set(validation?.broken_space_ids ?? []), [validation])
    const validCount = spaces.filter((sp) => !broken.has(sp.id)).length

    const groups = useMemo(() => {
        const byCode = new Map<string, { code: string; severity: string; count: number }>()
        for (const is of validation?.issues ?? []) {
            const g = byCode.get(is.code) ?? { code: is.code, severity: is.severity, count: 0 }
            g.count++
            byCode.set(is.code, g)
        }
        return [...byCode.values()].sort((a, b) =>
            (a.severity === 'error' ? 0 : 1) - (b.severity === 'error' ? 0 : 1) || b.count - a.count)
    }, [validation])

    if (!proposal) return null

    const provenance = proposal.provenance
    const date = formatDate(proposal.created_at)
    const author = provenance.method === 'agent'
        ? t('planEditor.proposals.by_agent', { mission: provenance.mission ?? '—', model: provenance.model ?? '—', date })
        : t('planEditor.proposals.by', { name: proposal.created_by?.name ?? t('planEditor.proposals.someone'), date })

    const decide = async (run: () => Promise<unknown>, accepted: boolean) => {
        setRefusal(null)
        try {
            await run()
            await onDecided(accepted)
        } catch (err) {
            const body = err as { code?: string }
            setRefusal({ uid: proposal.uid, code: body?.code ?? '', message: apiErrorMessage(err) })
        }
    }
    const onAccept = (keep: 'all' | 'valid', replace = false) => decide(() => accept.mutateAsync({
        params: { path: { orgCode: org, proposalUid: proposal.uid } },
        body: { keep, replace },
    }), true)
    const onDiscard = () => decide(() => discard.mutateAsync({
        params: { path: { orgCode: org, proposalUid: proposal.uid } },
    }), false)

    return (
        <AlertDialog open={open}>
            <AlertDialogContent data-testid="plan-editor-proposal-dialog" className="max-w-xl">
                <AlertDialogHeader>
                    <AlertDialogTitle>{proposal.title || t('planEditor.proposals.title')}</AlertDialogTitle>
                    <AlertDialogDescription data-testid="plan-editor-proposal-author">{author}</AlertDialogDescription>
                </AlertDialogHeader>

                {typeof value.calibration?.mm_per_unit === 'number' && (
                    <p className="text-xs" data-testid="plan-editor-proposal-calibration">
                        {t('planEditor.proposals.calibration', { mm: value.calibration.mm_per_unit })}
                    </p>
                )}

                {(value.layers ?? []).length > 0 && (
                    <ul className="space-y-1 text-xs">
                        {(value.layers ?? []).map((layer, index) => {
                            const fit = validation.layers.find((l) => l.index === index)
                            const name = layer.representation_uid
                                ? (layerLabelOf(layer.representation_uid) ?? t('planEditor.proposals.layer_existing'))
                                : t('planEditor.proposals.layer_page', { title: layer.title || '—', page: layer.page ?? 1 })
                            return (
                                <li key={index} className="flex items-center gap-2" data-testid={`plan-editor-proposal-layer-${index}`}>
                                    <span className="font-medium">
                                        {layer.discipline
                                            ? t(`planEditor.disciplines.${layer.discipline}`)
                                            : t('planEditor.proposals.no_discipline')}
                                    </span>
                                    <span className="truncate text-muted-foreground">{name}</span>
                                    <span className="ml-auto shrink-0" data-testid={`plan-editor-proposal-layer-residual-${index}`}>
                                        {fit
                                            ? t('planEditor.proposals.residual', { mm: Math.round(fit.residual_mm) })
                                            : t('planEditor.proposals.no_residual')}
                                    </span>
                                </li>
                            )
                        })}
                    </ul>
                )}

                {validation.placement && (
                    <p className="text-xs" data-testid="plan-editor-proposal-placement">
                        {t('planEditor.proposals.placement', { mm: Math.round(validation.placement.residual_mm) })}
                    </p>
                )}

                {bundle && (
                    <>
                        <PlanPreviewThumbnail spaces={spaces} broken={broken} testId="plan-editor-proposal-thumbnail" />
                        <p className="text-xs text-muted-foreground" data-testid="plan-editor-proposal-counts">
                            {t('planEditor.proposals.bundle', {
                                spaces: spaces.length,
                                walls: bundle.walls?.length ?? 0,
                                openings: bundle.openingEquipments?.length ?? 0,
                                equipments: bundle.equipments?.length ?? 0,
                            })}
                        </p>
                        <ul className="max-h-40 space-y-1 overflow-y-auto text-xs" data-testid="plan-editor-proposal-issues">
                            {groups.map((g) => (
                                <li
                                    key={g.code}
                                    className={g.severity === 'error' ? 'text-destructive' : 'text-muted-foreground'}
                                    data-testid={`plan-editor-proposal-issue-${g.code}`}
                                >
                                    <span className="font-medium">
                                        {(FLOORPLAN_ISSUE_CODES as readonly string[]).includes(g.code) ? t(`planEditor.floorplanIssues.${g.code}`) : g.code}
                                    </span>
                                    {' '}× {g.count}
                                </li>
                            ))}
                        </ul>
                    </>
                )}

                {(proposal.pending_assets?.length ?? 0) > 0 && (
                    <div className="text-xs" data-testid="plan-editor-proposal-pending-assets">
                        <p className="font-medium">
                            {t('planEditor.proposals.pendingAssets', { count: proposal.pending_assets?.length ?? 0 })}
                        </p>
                        <ul className="mt-1 max-h-32 list-disc space-y-0.5 overflow-y-auto pl-4 text-muted-foreground">
                            {proposal.pending_assets?.map((a) => (
                                <li key={a.uid} data-testid={`plan-editor-proposal-pending-asset-${a.uid}`}>{a.name || a.asset_type_code}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {current && (
                    <p className="text-xs text-destructive" data-testid="plan-editor-proposal-error">
                        {current.code === 'PLAN_CHANGED'
                            ? t('planEditor.proposals.plan_changed')
                            : current.code === 'EXTRACTION_EXISTS'
                                ? t('planEditor.proposals.extraction_exists')
                                : current.message}
                    </p>
                )}
                {proposals.length > 1 && (
                    <p className="text-xs text-muted-foreground" data-testid="plan-editor-proposal-next">
                        {t('planEditor.proposals.next', { count: proposals.length - 1 })}
                    </p>
                )}

                <AlertDialogFooter className="gap-2 sm:gap-2">
                    <AlertDialogCancel data-testid="plan-editor-proposal-close" disabled={busy} onClick={onClose}>
                        {t('actions.close', { ns: 'common' })}
                    </AlertDialogCancel>
                    <Button
                        variant="outline"
                        data-testid="plan-editor-proposal-discard"
                        disabled={busy}
                        onClick={() => { void onDiscard() }}
                    >
                        {t('planEditor.proposals.discard')}
                    </Button>
                    {broken.size > 0 && (
                        <Button
                            variant="outline"
                            data-testid="plan-editor-proposal-keep-valid"
                            disabled={busy || validCount === 0}
                            onClick={() => { void onAccept('valid', current?.code === 'EXTRACTION_EXISTS') }}
                        >
                            {t('planEditor.proposals.keep_valid', { count: validCount })}
                        </Button>
                    )}
                    {current?.code === 'EXTRACTION_EXISTS' ? (
                        <Button
                            data-testid="plan-editor-proposal-replace"
                            disabled={busy}
                            onClick={() => { void onAccept('all', true) }}
                        >
                            {t('planEditor.proposals.replace')}
                        </Button>
                    ) : (
                        <Button
                            data-testid="plan-editor-proposal-accept"
                            disabled={busy || current?.code === 'PLAN_CHANGED'}
                            onClick={() => { void onAccept('all') }}
                        >
                            {t('planEditor.proposals.accept')}
                        </Button>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
